import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { merge, of, ReplaySubject } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';

/**
 * Info icon that explains why a form field matters (e.g. "Required for SMS").
 * Shown while an enabled control is empty or invalid and hidden once it holds a valid value,
 * so the guidance disappears as soon as the user has satisfied it. Disabled controls never
 * show it: there is nothing the user can act on.
 */
@Component({
  selector: 'csi-field-hint',
  templateUrl: './field-hint.component.html',
  styleUrls: ['./field-hint.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldHintComponent implements OnInit, OnDestroy {
  /** Transloco key of the guidance text. */
  @Input() hintKey = '';
  @Input() set control(control: AbstractControl) {
    this.control$.next(control);
  }

  isVisible = false;

  private readonly control$ = new ReplaySubject<AbstractControl>(1);

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit() {
    this.control$
      .pipe(
        switchMap(control =>
          control
            ? merge(of(null), control.valueChanges, control.statusChanges).pipe(mapTo(control))
            : of(null)
        ),
        map(control => this.shouldShowHint(control)),
        untilDestroyed(this)
      )
      .subscribe(isVisible => {
        this.isVisible = isVisible;
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy() {}

  private shouldShowHint(control: AbstractControl | null): boolean {
    return !!control && control.enabled && !(control.valid && control.value);
  }
}
